// Client sheet C10 (G-B1): a discount rule is for all stores or only the
// stores the owner ticks. No stores saved = every store, like vouchers.

export type StoreScope = 'ALL' | 'SELECTED';

export const NO_STORE_TICKED_ERROR = 'Tick at least one store, or choose All stores.';

/** How the form opens for a saved rule (edit and copy). */
export function scopeFromRule(locationIds: string[] | null | undefined): {
  scope: StoreScope;
  locationIds: string[];
} {
  const ids = locationIds ?? [];
  return ids.length > 0
    ? { scope: 'SELECTED', locationIds: [...ids] }
    : { scope: 'ALL', locationIds: [] };
}

/**
 * The `location_ids` the form saves. Always a list, never undefined: `[]`
 * widens a rule back to every store, which an omitted field would not do.
 */
export function locationIdsForSave(
  scope: StoreScope,
  ticked: string[],
): { locationIds: string[] } | { error: string } {
  if (scope === 'ALL') return { locationIds: [] };
  const ids = [...new Set(ticked)];
  return ids.length > 0 ? { locationIds: ids } : { error: NO_STORE_TICKED_ERROR };
}

/** The list badge: "1 store" / "2 stores"; null when the rule is for every store. */
export function storeCountLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 store' : `${count} stores`;
}

export interface StoreOption {
  id: string;
  label: string;
}

/**
 * The stores the form offers: the active ones, plus any switched-off store the
 * rule already has (marked "(inactive)"), so a saved store always shows ticked.
 */
export function storeOptions(
  locations: Array<{ id: string; name: string; location_code?: string | null; is_active: boolean }>,
  savedIds: string[],
): StoreOption[] {
  return locations
    .filter((l) => l.is_active || savedIds.includes(l.id))
    .map((l) => {
      const base = l.location_code ? `${l.name} (${l.location_code})` : l.name;
      return { id: l.id, label: l.is_active ? base : `${base} (inactive)` };
    });
}
